import {
  createContext,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { tenantKeys } from "@/domains/tenant/queries";
import { supabase } from "@/shared/lib/supabase";
import { isSupabaseUserNotFoundError } from "@/shared/lib/supabase-auth-errors";

export type SessionContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: {
    id: string;
    email?: string;
  } | null;
};

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const identifiedUserId = useRef<string | null>(null);
  const [user, setUser] = useState<SessionContextValue["user"]>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const identifyUser = (
      authenticatedUser: NonNullable<SessionContextValue["user"]>,
    ) => {
      if (
        !posthog.__loaded ||
        identifiedUserId.current === authenticatedUser.id
      ) {
        return;
      }

      if (identifiedUserId.current) {
        posthog.reset();
      }

      posthog.identify(
        authenticatedUser.id,
        authenticatedUser.email
          ? { email: authenticatedUser.email }
          : undefined,
      );
      identifiedUserId.current = authenticatedUser.id;
    };

    const resetIdentity = () => {
      if (posthog.__loaded) {
        posthog.reset();
      }
      identifiedUserId.current = null;
    };

    const syncSessionUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!isMounted) return;
      if (!session?.user) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const {
        data: { user: verifiedUser },
        error,
      } = await supabase.auth.getUser();

      if (!isMounted) return;
      if (error && isSupabaseUserNotFoundError(error)) {
        resetIdentity();
        await supabase.auth.signOut();
        if (!isMounted) return;
        setUser(null);
        setIsLoading(false);
        return;
      }

      const authenticatedUser = verifiedUser ?? session.user;
      identifyUser(authenticatedUser);
      setUser(authenticatedUser);
      setIsLoading(false);
    };

    void syncSessionUser();

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        resetIdentity();
        queryClient.removeQueries({ queryKey: tenantKeys.current().queryKey });
      } else if (session?.user) {
        identifyUser(session.user);
      }
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({
      isLoading,
      isAuthenticated: !!user,
      user,
    }),
    [user, isLoading],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession() {
  const ctx = use(SessionContext);
  if (!ctx)
    throw new Error("useSessionContext must be used within SessionProvider");
  return ctx;
}
